import { ESdChangeReason, IWFParameter, IWFStep, IWorkFlow } from "@kuika/kuika-cl-model";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaCheckbox from "../../../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaDropdownMenu from "../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { getWorkflowParamTypeLabel } from "../../../left-panel/work-flow-list/workflow-parameter-type-options";

export interface IWorkflowStepOutputMappingState {
  stepOutputMapValues: IWFParameter[];
  selectedWfParamToMap: IWFParameter[];
  paramMapMenuVisible: boolean;
  searchText: string;
}

export interface IWorkflowStepOutputMappingProps {
  wf: IWorkFlow;
  step: IWFStep;
  onUpdateSelectedParamsToMap: (paramsToMap: IWFParameter[]) => void;
  /** Render the picker trigger as a compact "+" icon (a section-header affordance) instead of the full-width text button. */
  triggerIcon?: boolean;
  /** Class applied to the icon trigger so the host styles it (e.g. the section-header "+"). */
  triggerClassName?: string;
}

class WorkflowStepOutputMapping extends PureSdComponent<
  IWorkflowStepOutputMappingProps & IWithSDProps & IWithContextProp,
  IWorkflowStepOutputMappingState
> {
  constructor(props: IWorkflowStepOutputMappingProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.onUpdateSelectedParamsToMap = this.onUpdateSelectedParamsToMap.bind(this);
    this.state = {
      stepOutputMapValues: [] as IWFParameter[],
      selectedWfParamToMap: [] as IWFParameter[],
      paramMapMenuVisible: false,
      searchText: ""
    };
  }

  onUpdateSelectedParamsToMap = (selectedParams) => {
    this.props.onUpdateSelectedParamsToMap(selectedParams);
    this.setState({
      paramMapMenuVisible: false,
      selectedWfParamToMap: selectedParams,
      stepOutputMapValues: selectedParams
    });
  };

  componentDidMount(): void {
    this.getMappedWfParams();
  }

  componentDidUpdate(prevProps: IWithSDProps): void {
    if (prevProps.sd.selectedWfNode?.id !== this.props.sd.selectedWfNode?.id) this.getMappedWfParams();
  }

  getMappedWfParams() {
    const wf = this.props.sd.selectedWf;
    const step = this.props.sd.getSelectedWfStep();
    if (wf && wf.parameters && step && step.outputParameters) {
      const mappings = wf.parameters.filter((p) => {
        return step.outputParameters?.some((o) => o.id === p.id);
      });
      this.setState({
        stepOutputMapValues: mappings,
        selectedWfParamToMap: mappings
      });
    }
  }

  onParamChangeHandle = (e, item) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();
    const selectedWfParamToMap = _.cloneDeep(this.state.selectedWfParamToMap);
    if (e.target.checked === true) selectedWfParamToMap.push(item);
    else {
      const index = selectedWfParamToMap.findIndex((i) => i.id === item.id);
      selectedWfParamToMap.splice(index, 1);
    }
    this.setState({ selectedWfParamToMap });
  };

  handleParamMenuVisibilityChange = (e) => {
    if (e === true) this.getMappedWfParams();
    this.setState({ paramMapMenuVisible: e });
  };

  handleOutputSearchInput = (e) => {
    const value = e.target.value.toLowerCase();
    this.setState({ searchText: value });
  };

  render() {
    const searchHeader = (
      <div style={{ width: "100%" }}>
        <KuikaInput
          placeholder="Search..."
          value={this.state.searchText}
          onChange={this.handleOutputSearchInput}
          size="md"
          style={{ width: "100%" }}
          autoFocus
        />
      </div>
    );

    const saveFooter = (
      <KuikaButton
        type="text"
        size="sm"
        onClick={() => this.onUpdateSelectedParamsToMap(this.state.selectedWfParamToMap)}
      >
        SAVE
      </KuikaButton>
    );

    const wfParameters =
      this.props.wf.parameters && this.props.wf.parameters.length > 0 ? this.props.wf.parameters : [];

    const businessObjectParams = wfParameters.filter(
      (wfp) =>
        wfp.isBusinessObject === true &&
        (this.state.searchText === "" || wfp.name.toLowerCase().includes(this.state.searchText.toLowerCase()))
    );

    const internalParams = wfParameters.filter(
      (wfp) =>
        wfp.isBusinessObject === false &&
        (this.state.searchText === "" || wfp.name.toLowerCase().includes(this.state.searchText.toLowerCase()))
    );

    const dropdownOptions = [
      ...businessObjectParams.map((parameter: IWFParameter, index: number) => ({
        key: `bo-${parameter.id}`,
        value: parameter.id,
        label: (
          <KuikaCheckbox
            checked={this.state.selectedWfParamToMap.some((p) => p.id === parameter.id)}
            onChange={(e) => this.onParamChangeHandle(e, parameter)}
            label={`${parameter.name} (${getWorkflowParamTypeLabel(parameter)})`}
          />
        ),
        group: "Business Objects",
        // The group is part of the id: which category a parameter is listed under is what tests read.
        dataTestId: `stepOutputsBusinessObjectOption-${parameter.name}`,
        onClick: () => {
          const isChecked = this.state.selectedWfParamToMap.some((p) => p.id === parameter.id);
          this.onParamChangeHandle({ target: { checked: !isChecked } }, parameter);
        },
        showSelectedIcon: false
      })),
      ...internalParams.map((parameter: IWFParameter, index: number) => ({
        key: `internal-${parameter.id}`,
        value: parameter.id,
        label: (
          <KuikaCheckbox
            checked={this.state.selectedWfParamToMap.some((p) => p.id === parameter.id)}
            onChange={(e) => this.onParamChangeHandle(e, parameter)}
            label={`${parameter.name} (${getWorkflowParamTypeLabel(parameter)})`}
          />
        ),
        group: "Internal Parameters",
        dataTestId: `stepOutputsInternalParameterOption-${parameter.name}`,
        onClick: () => {
          const isChecked = this.state.selectedWfParamToMap.some((p) => p.id === parameter.id);
          this.onParamChangeHandle({ target: { checked: !isChecked } }, parameter);
        },
        showSelectedIcon: false
      }))
    ];

    return (
      <KuikaDropdownMenu
        options={dropdownOptions}
        open={this.state.paramMapMenuVisible}
        setOpen={this.handleParamMenuVisibilityChange}
        header={searchHeader}
        footer={saveFooter}
        overlayClassName="kuika-select-parameters-dropdown"
        menuTestId="stepOutputsParameterMenu"
        placement="bottomLeft"
        trigger="click"
        closeOnSelect={false}
      >
        {() =>
          this.props.triggerIcon ? (
            <button
              type="button"
              className={this.props.triggerClassName}
              aria-label="Map step outputs"
              data-testid="stepOutputsMapTrigger"
              onClick={() => this.setState({ paramMapMenuVisible: !this.state.paramMapMenuVisible })}
            >
              <KuikaSvg name="plus" width={14} height={14} color="var(--dark-7)" />
            </button>
          ) : (
            <div
              data-testid="stepOutputsMapTrigger"
              style={{
                display: "flex",
                cursor: "pointer",
                padding: "0",
                height: 32,
                width: "100%"
              }}
              onClick={() => this.setState({ paramMapMenuVisible: !this.state.paramMapMenuVisible })}
            >
              <span className="kuika_workflow-properties__primary-text">+ SELECT PARAMETERS TO MAP</span>
            </div>
          )
        }
      </KuikaDropdownMenu>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return true;
  };
}

const workflowStepOutputMapping = withContext<IWorkflowStepOutputMappingProps>(
  withSD<IWorkflowStepOutputMappingProps>(WorkflowStepOutputMapping)
);
export { workflowStepOutputMapping as WorkflowStepOutputMapping };
